import { Avatar, Box, Divider, Grid, Modal, Stack, Typography } from "@mui/material";
import { UPDATE_CUSTOM_BADGES } from "lib/constants";
import MDButton from "components/MDButton";
import { postAPICall } from "lib/callApi";
import AutocompleteDark from "utils/AutocompleteDark";

import { useEffect, useRef, useState } from "react";
import badgeImageMap from "utils/Badges/badgeImageMap";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: window.innerWidth < 500 ? "90%" : 700,
  backgroundColor: "#2c2c2c",
  boxShadow: 24,
  p: 4,
  overflowY: "auto",
  borderRadius: "0.5rem",
  outline: "none",
};

function CustomizeBadgeModal({
  open,
  handleClose,
  customizeBadgeData,
  showNotification,
  selectedUser,
  setBadges,
  setUserBadgeData,
}) {
  const { companyKpis, customBadgeMap } = customizeBadgeData;
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [badgeKpiMap, setBadgeKpiMap] = useState(
    Object.keys(customBadgeMap).reduce((acc, key) => {
      acc[key] = companyKpis.find((kpi) => kpi.id === customBadgeMap[key].kpiActionId) || null;
      return acc;
    }, {})
  );
  const originalBadgeMapRef = useRef(null);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (originalBadgeMapRef.current === null) {
      originalBadgeMapRef.current = badgeKpiMap;
    }
  }, [badgeKpiMap]);

  const handleKpiChange = (badgeKey, newValue) => {
    setBadgeKpiMap((prev) => ({
      ...prev,
      [badgeKey]: newValue || null,
    }));
  };

  const handleFinalSubmit = async (modifiedEntries) => {
    setIsConfirming(false);
    const response = await postAPICall(UPDATE_CUSTOM_BADGES, true, {
      modifiedEntries,
      selectedUser,
    });
    if (response?.data?.success) {
      setBadges(response.data.badges);
      setUserBadgeData(response.data.userBadgeData);

      showNotification("success", "Custom badges updated!");
      setErrorMessage("");
      handleClose();
    } else {
      setErrorMessage(response?.data?.message || "Something went wrong, please try again");
    }
    setIsLoading(false);
  };

  const handleSubmit = () => {
    setErrorMessage("");

    // Validate inputs for duplicate KPIs
    const kpiCount = {};
    Object.entries(badgeKpiMap).forEach(([, kpi]) => {
      if (kpi && kpi.id) {
        if (kpiCount[kpi.id]) {
          kpiCount[kpi.id].count += 1;
        } else {
          kpiCount[kpi.id] = { count: 1, name: kpi.name };
        }
      }
    });

    const duplicates = Object.values(kpiCount).filter((entry) => entry.count > 1);
    if (duplicates.length > 0) {
      const duplicateNames = duplicates.map((entry) => entry.name).join(", ");
      setErrorMessage(`Remove duplicate KPIs: ${duplicateNames}`);
      return;
    }

    // Check if any KPIs have changed from the original values
    const modifiedEntries = [];
    const entriesToConfirm = Object.entries(badgeKpiMap).filter(([key, kpi]) => {
      const originalKpi = originalBadgeMapRef.current[key];
      const diff = originalKpi?.id !== kpi?.id;
      if (diff) modifiedEntries.push({ customBadgeKey: key, newKpi: kpi, originalKpi });
      if (!originalKpi) return false;
      return diff;
    });

    // if any KPIs have changed from one KPI to another (or null), show confirmation dialog
    if (!isConfirming && entriesToConfirm.length > 0) {
      setIsConfirming(true);
      setErrorMessage(
        "Some KPIs have been changed. Any badges that have been awarded using the previous KPI mappings will be removed. Are you sure?"
      );
      return;
    }

    if (modifiedEntries.length) {
      setIsLoading(true);
      handleFinalSubmit(modifiedEntries);
    }
  };

  const getSubmitButtonText = () => {
    if (isLoading) return "Loading...";
    return isConfirming ? "Confirm" : "Save";
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Stack sx={{ mb: "1rem" }}>
          <Typography variant="h4" sx={{ color: "#b9b9b9" }}>
            Customize Badges
          </Typography>
          <Typography sx={{ lineHeight: "1rem", fontSize: "0.9rem", color: "#a2a2a2" }}>
            Assign KPIs to enable custom badges
          </Typography>
        </Stack>
        <Divider sx={{ mt: 2, mb: 2, borderColor: "#fff", borderWidth: "2px" }} />
        <Box>
          {Object.keys(customBadgeMap).map((groupId) => (
            <Grid
              container
              key={groupId}
              spacing={2}
              sx={{ mb: 2, width: "100%", alignItems: "center" }}
            >
              <Grid item xs={12} sm={6}>
                <AutocompleteDark
                  options={companyKpis}
                  getOptionLabel={(option) => option.name}
                  value={
                    companyKpis.find((option) => option.id === badgeKpiMap[groupId]?.id) || null
                  }
                  onChange={(event, newValue) => handleKpiChange(groupId, newValue)}
                  label="Select KPI"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {Object.values(customBadgeMap[groupId].definitions).map((badge, index) => (
                    <Avatar
                      key={badge.id}
                      src={badgeImageMap[customBadgeMap[groupId].definitions[index].badge_image]}
                      alt={badge.badge_image}
                      sx={{
                        width: 75,
                        height: 75,
                        marginLeft: index !== 0 ? -5 : 0,
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          ))}
        </Box>
        <Divider sx={{ mt: 2, mb: 2, borderColor: "#fff", borderWidth: "2px" }} />
        <Stack direction="row" alignItems="center" mt={2} justifyContent="space-between">
          <Typography color="error" fontSize="0.9rem" lineHeight="1rem">
            {errorMessage}
          </Typography>
          <Box sx={{ display: "flex" }}>
            <MDButton
              onClick={handleClose}
              color="gray80"
              style={{ marginRight: "10px", border: "2px solid #444" }}
            >
              Cancel
            </MDButton>
            <MDButton onClick={handleSubmit} color="primaryRed" disabled={isLoading}>
              {getSubmitButtonText()}
            </MDButton>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
}

export default CustomizeBadgeModal;
